'use client';

import React, { useState, useEffect } from 'react';
import { CalendarX2, Plus, Trash2, X } from 'lucide-react';

interface TimeBlocksTabProps {
  staffList: { id: string; name: string }[];
}

export function TimeBlocksTab({ staffList }: TimeBlocksTabProps) {
  const [blocks, setBlocks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState('');
  const [staffId, setStaffId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchBlocks = async () => {
    try {
      const res = await fetch('/api/timeblocks');
      const data = await res.json();
      if (data.success) {
        setBlocks(data.timeBlocks || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlocks();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('¿Deseas desbloquear este período?')) return;
    try {
      const res = await fetch(`/api/timeblocks?id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchBlocks();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !startDate || !endDate) return;

    try {
      const res = await fetch('/api/timeblocks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          staffId: staffId || null,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString()
        })
      });
      const data = await res.json();
      if (data.success) {
        setTitle('');
        setShowModal(false);
        fetchBlocks();
      }
    } catch (err) {
      alert('Error al guardar bloqueo');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600 font-medium">
          Períodos no laborables, feriados o ausencias programadas del personal.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo bloqueo</span>
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-xs text-slate-400">Cargando bloqueos...</div>
      ) : (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm divide-y divide-slate-100 overflow-hidden">
          {blocks.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400 italic">
              No hay bloqueos activos de agenda.
            </div>
          ) : (
            blocks.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50/60 transition-colors"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center border border-amber-100/80">
                    <CalendarX2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">{b.title}</h4>
                    <p className="text-[11px] text-slate-500">
                      {b.staff ? `Aplica a: ${b.staff.name}` : 'Aplica a todo el negocio'} ·{' '}
                      {new Date(b.startDate).toLocaleDateString('es-AR')} al{' '}
                      {new Date(b.endDate).toLocaleDateString('es-AR')}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleDelete(b.id)}
                  className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                  title="Eliminar bloqueo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs">
          <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-md w-full space-y-4 border border-slate-100">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-sm font-bold text-slate-900">Nuevo Bloqueo de Agenda</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Motivo / Título</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Feriado Nacional, Vacaciones..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Profesional afectado</label>
                <select
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                >
                  <option value="">Todo el negocio (Cierre general)</option>
                  {staffList.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Fecha Inicio</label>
                  <input
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Fecha Fin</label>
                  <input
                    type="date"
                    required
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs text-slate-600 font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl"
                >
                  Crear Bloqueo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
